import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
} from 'material-react-table';
import ApiAnalyticsService, { type ApiEvent } from '../../../Services/ApiAnalyticsService';
import Notification from '../../Layout/Notification';
import { useTheme } from '@mui/material/styles';
import { extractRows, extractTotal, formatDuration, formatTimestamp } from './apiAnalyticsUtils';

export interface EventsTarget {
  clientId: string;
  httpMethod: string;
  endpoint: string;
}

interface ApiEventsDialogProps {
  target: EventsTarget | null;
  from: string | null;
  to: string | null;
  onClose: () => void;
}

// Table column ids that don't match their backend (entity) property name for sorting.
const EVENT_SORT_FIELDS: Record<string, string> = { timestamp: 'createdAt' };

function statusColor(status: number | null): 'success' | 'warning' | 'error' | 'default' {
  if (status === null) return 'default';
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  return 'success';
}

const ApiEventsDialog = ({ target, from, to, onClose }: ApiEventsDialogProps) => {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'timestamp', desc: true }]);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    // reset paging whenever a new endpoint is opened
    setPage(0);
  }, [target?.clientId, target?.httpMethod, target?.endpoint]);

  const fetchEvents = useCallback(async () => {
    if (!target) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    try {
      const sortOrder = sorting[0];
      const sort = sortOrder
        ? `${EVENT_SORT_FIELDS[sortOrder.id] ?? sortOrder.id},${sortOrder.desc ? 'desc' : 'asc'}`
        : null;
      const response = await ApiAnalyticsService.getClientApiEvents(
        target.clientId,
        target.httpMethod,
        target.endpoint,
        {
          from,
          to,
          page,
          size: rowsPerPage,
          sort,
        }
      );
      if (id !== fetchIdRef.current) return;
      setEvents(extractRows<ApiEvent>(response));
      setTotal(extractTotal(response));
    } catch (err: unknown) {
      if (id !== fetchIdRef.current) return;
      Notification.fromError(err, { fallbackTitle: "Couldn't load events" });
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [target, from, to, page, rowsPerPage, sorting]);

  useEffect(() => {
    if (target) fetchEvents();
  }, [target, fetchEvents]);

  const columns = useMemo<MRT_ColumnDef<ApiEvent>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        Cell: ({ cell }) => formatTimestamp(cell.getValue<string | null>()),
        size: 190,
      },
      {
        accessorKey: 'httpStatus',
        header: 'Status',
        size: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue<number | null>();
          return (
            <Chip
              label={status ?? '-'}
              color={statusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'durationMs',
        header: 'Duration',
        size: 110,
        Cell: ({ cell }) => formatDuration(cell.getValue<number | null>()),
      },
      { accessorKey: 'endpoint', header: 'Request URI', size: 260 },
      { accessorKey: 'principal', header: 'User', size: 140 },
      {
        accessorKey: 'errorMessage',
        header: 'Error',
        size: 240,
        Cell: ({ cell }) => cell.getValue<string | null>() || '-',
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    data: events,
    columns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: total,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableTopToolbar: false,
    enableStickyHeader: true,
    initialState: { density: 'compact' },
    state: {
      pagination: { pageIndex: page, pageSize: rowsPerPage },
      sorting,
      isLoading: loading,
    },
    onSortingChange: updater => {
      setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
      setPage(0);
    },
    onPaginationChange: updater => {
      const prev = { pageIndex: page, pageSize: rowsPerPage };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPage(next.pageIndex);
      setRowsPerPage(next.pageSize);
    },
    paginationDisplayMode: 'pages',
    muiTablePaperProps: { elevation: 0 },
    muiTableContainerProps: { sx: { maxHeight: '55vh' } },
    muiTableHeadCellProps: {
      sx: { fontWeight: 700, fontSize: 13, borderBottom: `2px solid ${ACCENT}` },
    },
    muiTableBodyCellProps: { sx: { fontSize: 13 } },
    renderEmptyRowsFallback: () => (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        No events for this endpoint in the selected time range.
      </Box>
    ),
  });

  return (
    <Dialog open={target !== null} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography component="span" sx={{ fontWeight: 700 }}>
          Event history
        </Typography>
        {target && (
          <Typography component="div" sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
            <Chip label={target.httpMethod} size="small" sx={{ mr: 1 }} />
            {target.endpoint}
          </Typography>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MaterialReactTable table={table} />
      </DialogContent>
    </Dialog>
  );
};

export default ApiEventsDialog;
